import type { LatLngTuple } from "@fortex/api";
import { degrees, radians } from "./geodesy";

type Ellipsoid = {
  a: number;
  e: number;
  e2: number;
};

type Vector = [number, number, number];

function ellipsoid(a: number, inverseFlattening: number): Ellipsoid {
  const flattening = 1 / inverseFlattening;
  const e2 = 2 * flattening - flattening * flattening;

  return { a, e2, e: Math.sqrt(e2) };
}

export const wgs84Ellipsoid = ellipsoid(6378137, 298.257223563);

export const grs67Ellipsoid = ellipsoid(6378160, 298.247167427);

export const hd72ToWgs84 = {
  dx: 52.684,
  dy: -71.194,
  dz: -13.975,
  rx: 0.312,
  ry: 0.1063,
  rz: 0.3729,
  ds: 1.0191
};

export const eovProjection = {
  lat0: radians(47.1443937222222),
  lon0: radians(19.0485717777778),
  k0: 0.99993,
  falseEasting: 650000,
  falseNorthing: 200000
};

const quarterPi = Math.PI / 4;

const halfPi = Math.PI / 2;

const arcSecond = Math.PI / (180 * 3600);

const iterationLimit = 6;

const epsilon = 1e-10;

function clampedAsin(value: number): number {
  return Math.asin(Math.max(-1, Math.min(1, value)));
}

function geodeticToGeocentric(shape: Ellipsoid, lat: number, lon: number): Vector {
  const sinLat = Math.sin(lat);
  const cosLat = Math.cos(lat);
  const normal = shape.a / Math.sqrt(1 - shape.e2 * sinLat * sinLat);

  return [normal * cosLat * Math.cos(lon), normal * cosLat * Math.sin(lon), normal * (1 - shape.e2) * sinLat];
}

function geocentricToGeodetic(shape: Ellipsoid, vector: Vector): [number, number] {
  const [x, y, z] = vector;
  const distance = Math.sqrt(x * x + y * y);
  const lon = Math.atan2(y, x);
  let lat = Math.atan2(z, distance * (1 - shape.e2));

  for (let index = 0; index < 12; index++) {
    const sinLat = Math.sin(lat);
    const normal = shape.a / Math.sqrt(1 - shape.e2 * sinLat * sinLat);
    const height = distance / Math.cos(lat) - normal;
    const next = Math.atan2(z, distance * (1 - (shape.e2 * normal) / (normal + height)));

    if (Math.abs(next - lat) < 1e-14) {
      lat = next;
      break;
    }

    lat = next;
  }

  return [lat, lon];
}

function rotationMatrix(): number[][] {
  const rx = hd72ToWgs84.rx * arcSecond;
  const ry = hd72ToWgs84.ry * arcSecond;
  const rz = hd72ToWgs84.rz * arcSecond;

  return [
    [1, rz, -ry],
    [-rz, 1, rx],
    [ry, -rx, 1]
  ];
}

const frame = rotationMatrix();

const scale = 1 + hd72ToWgs84.ds * 1e-6;

function multiply(matrix: number[][], vector: Vector): Vector {
  return [
    matrix[0][0] * vector[0] + matrix[0][1] * vector[1] + matrix[0][2] * vector[2],
    matrix[1][0] * vector[0] + matrix[1][1] * vector[1] + matrix[1][2] * vector[2],
    matrix[2][0] * vector[0] + matrix[2][1] * vector[1] + matrix[2][2] * vector[2]
  ];
}

function inverse(matrix: number[][]): number[][] {
  const [[a, b, c], [d, e, f], [g, h, i]] = matrix;
  const determinant = a * (e * i - f * h) - b * (d * i - f * g) + c * (d * h - e * g);

  return [
    [(e * i - f * h) / determinant, (c * h - b * i) / determinant, (b * f - c * e) / determinant],
    [(f * g - d * i) / determinant, (a * i - c * g) / determinant, (c * d - a * f) / determinant],
    [(d * h - e * g) / determinant, (b * g - a * h) / determinant, (a * e - b * d) / determinant]
  ];
}

const frameInverse = inverse(frame);

function hd72ToWgs84Geocentric(vector: Vector): Vector {
  const rotated = multiply(frame, vector);

  return [scale * rotated[0] + hd72ToWgs84.dx, scale * rotated[1] + hd72ToWgs84.dy, scale * rotated[2] + hd72ToWgs84.dz];
}

function wgs84ToHd72Geocentric(vector: Vector): Vector {
  const shifted: Vector = [(vector[0] - hd72ToWgs84.dx) / scale, (vector[1] - hd72ToWgs84.dy) / scale, (vector[2] - hd72ToWgs84.dz) / scale];

  return multiply(frameInverse, shifted);
}

function somercSetup() {
  const shape = grs67Ellipsoid;
  const halfE = 0.5 * shape.e;
  const cosLat0 = Math.cos(eovProjection.lat0);
  const cp = cosLat0 * cosLat0;
  const c = Math.sqrt(1 + (shape.e2 * cp * cp) / (1 - shape.e2));
  const sinLat0 = Math.sin(eovProjection.lat0);
  const sinp0 = sinLat0 / c;
  const phip0 = clampedAsin(sinp0);
  const cosp0 = Math.cos(phip0);
  const sp = sinLat0 * shape.e;
  const K =
    Math.log(Math.tan(quarterPi + 0.5 * phip0)) -
    c * (Math.log(Math.tan(quarterPi + 0.5 * eovProjection.lat0)) - halfE * Math.log((1 + sp) / (1 - sp)));
  const kR = (eovProjection.k0 * Math.sqrt(1 - shape.e2)) / (1 - sp * sp);

  return { shape, halfE, c, sinp0, cosp0, K, kR };
}

const somerc = somercSetup();

function normalizeLongitude(value: number): number {
  let result = value;

  while (result > Math.PI) result -= 2 * Math.PI;
  while (result < -Math.PI) result += 2 * Math.PI;

  return result;
}

function somercForward(lat: number, lon: number): [number, number] {
  const lam = normalizeLongitude(lon - eovProjection.lon0);
  const sp = somerc.shape.e * Math.sin(lat);
  const phip =
    2 * Math.atan(Math.exp(somerc.c * (Math.log(Math.tan(quarterPi + 0.5 * lat)) - somerc.halfE * Math.log((1 + sp) / (1 - sp))) + somerc.K)) - halfPi;
  const lamp = somerc.c * lam;
  const cp = Math.cos(phip);
  const phipp = clampedAsin(somerc.cosp0 * Math.sin(phip) - somerc.sinp0 * cp * Math.cos(lamp));
  const lampp = clampedAsin((cp * Math.sin(lamp)) / Math.cos(phipp));

  return [somerc.kR * lampp * somerc.shape.a, somerc.kR * Math.log(Math.tan(quarterPi + 0.5 * phipp)) * somerc.shape.a];
}

function somercInverse(x: number, y: number): [number, number] {
  const phipp = 2 * (Math.atan(Math.exp(y / somerc.shape.a / somerc.kR)) - quarterPi);
  const lampp = x / somerc.shape.a / somerc.kR;
  const cp = Math.cos(phipp);
  let phip = clampedAsin(somerc.cosp0 * Math.sin(phipp) + somerc.sinp0 * cp * Math.cos(lampp));
  const lamp = clampedAsin((cp * Math.sin(lampp)) / Math.cos(phip));
  const con = (somerc.K - Math.log(Math.tan(quarterPi + 0.5 * phip))) / somerc.c;

  for (let index = 0; index < iterationLimit; index++) {
    const esp = somerc.shape.e * Math.sin(phip);
    const delp =
      (con + Math.log(Math.tan(quarterPi + 0.5 * phip)) - somerc.halfE * Math.log((1 + esp) / (1 - esp))) *
      (1 - esp * esp) *
      Math.cos(phip) *
      (1 / (1 - somerc.shape.e2));

    phip -= delp;

    if (Math.abs(delp) < epsilon) break;
  }

  return [phip, normalizeLongitude(lamp / somerc.c + eovProjection.lon0)];
}

export type EovPoint = {
  y: number;
  x: number;
};

export function wgs84ToEov(lat: number, lon: number): EovPoint {
  const geocentric = geodeticToGeocentric(wgs84Ellipsoid, radians(lat), radians(lon));
  const [hdLat, hdLon] = geocentricToGeodetic(grs67Ellipsoid, wgs84ToHd72Geocentric(geocentric));
  const [easting, northing] = somercForward(hdLat, hdLon);

  return { y: easting + eovProjection.falseEasting, x: northing + eovProjection.falseNorthing };
}

export function eovToWgs84(y: number, x: number): LatLngTuple {
  const [hdLat, hdLon] = somercInverse(y - eovProjection.falseEasting, x - eovProjection.falseNorthing);
  const geocentric = geodeticToGeocentric(grs67Ellipsoid, hdLat, hdLon);
  const [lat, lon] = geocentricToGeodetic(wgs84Ellipsoid, hd72ToWgs84Geocentric(geocentric));

  return [degrees(lat), degrees(lon)];
}

const locale = "hu-HU";

function metres(value: number): string {
  return `${value.toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m`;
}

export function formatEov(y: number, x: number): string {
  return `Y ${metres(y)}, X ${metres(x)}`;
}
